import { cva, type VariantProps } from 'class-variance-authority';
import {
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { cn } from '../../lib/utils.js';

export {
  Select,
  FilterClearButton,
  type SelectProps,
  type FilterClearButtonProps,
} from './select-dropdown.js';
export {
  PRODUCT_SORT_OPTIONS,
  PRODUCT_STATUS_FILTER_OPTIONS,
  PRODUCT_VALIDATION_FILTER_OPTIONS,
  ProductsSearchFilterBar,
  SearchFilterBar,
  type ProductStatusFilter,
  type ProductValidationFilter,
  type ProductsSearchFilterBarProps,
  type SearchFilterBarProps,
  type SearchSortOption,
} from './search-filter-bar.js';
export {
  SearchableMultiSelect,
  type SearchableMultiSelectProps,
} from './searchable-multi-select.js';
export {
  ModalDialog,
  ConfirmDialog,
  type ModalDialogProps,
  type ConfirmDialogProps,
} from './modal-dialog.js';
export { Checkbox, Switch, type CheckboxProps, type SwitchProps } from './checkbox-switch.js';
export {
  CONTEXT_MENU_DIVIDER,
  computeContextMenuPositionStyle,
  copyTextToClipboard,
  normalizeContextMenuEntries,
  openExternalHttpsUrl,
  shouldAllowNativeContextMenu,
  useContextMenu,
  useFloatingMenuDismiss,
  type ContextMenuActionItem,
  type ContextMenuDividerItem,
  type ContextMenuEntry,
  type NormalizedContextMenuItem,
  type UseContextMenuResult,
} from './context-menu.js';
export {
  Card,
  CardIcon,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardRow,
  Skeleton,
  ActionOverflowMenu,
  ToastNotification,
  computeVirtualizedSlice,
  type CardIconProps,
  type CardRowProps,
  type ActionOverflowMenuProps,
  type ToastNotificationProps,
  type VirtualizedSliceOptions,
  type VirtualizedSliceResult,
} from './surface-primitives.js';

const buttonVariants = cva(
  'box-border inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold leading-none align-middle transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:block [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0 cursor-pointer',
  {
    variants: {
      variant: {
        primary:
          'rounded-control border border-transparent bg-brand bg-clip-padding font-semibold text-brand-fg hover:bg-brand-hover',
        secondary:
          'rounded-control border border-border-medium bg-surface font-semibold text-fg hover:border-border-medium hover:bg-surface-subtle hover:text-fg',
        ghost:
          'rounded-control border border-transparent bg-clip-padding text-fg-secondary hover:bg-surface-subtle hover:text-fg',
        destructive:
          'rounded-control border border-status-error-border bg-status-error-bg font-semibold text-status-error hover:border-status-error',
      },
      size: {
        default: 'h-[36px] min-h-[36px] max-h-[36px] py-0 px-3.5 text-[13px] leading-none',
        sm: 'h-[36px] min-h-[36px] max-h-[36px] py-0 px-3.5 text-[13px] leading-none',
        xs: 'h-7 min-h-7 max-h-7 py-0 px-2.5 text-[11px] leading-none',
        lg: 'h-10 min-h-10 max-h-10 py-0 px-5 text-[14px] leading-none',
        rail: 'h-[52px] w-[52px] min-h-[52px] max-h-[52px] rounded-full p-0 shadow-primary-glow [&_svg]:h-5 [&_svg]:w-5',
        icon: 'h-[36px] w-[36px] min-h-[36px] max-h-[36px] p-0 leading-none',
        'icon-xs': 'h-7 w-7 min-h-7 max-h-7 p-0 leading-none',
        'icon-sm': 'h-6 w-6 min-h-6 max-h-6 p-0 leading-none',
        inline: 'h-auto p-0 whitespace-normal leading-normal',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  readonly ref?: Ref<HTMLButtonElement>;
}

export function Button({
  className,
  variant,
  size,
  type = 'button',
  ref,
  ...props
}: ButtonProps): ReactElement {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

const badgeVariants = cva(
  'box-border inline-flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-pill font-semibold tracking-[0.2px] leading-none align-middle transition-colors tabular-nums [&_svg]:block [&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'border border-subtle bg-surface-subtle text-fg',
        secondary: 'border border-subtle bg-surface-subtle text-fg-secondary',
        outline: 'border border-border bg-surface text-fg-secondary',
        accent: 'border border-brand-border-tint bg-brand-wash text-brand',
        valid: 'border border-status-valid-border bg-status-valid-bg text-status-valid',
        success: 'border border-status-valid-border bg-status-valid-bg text-status-valid',
        warning: 'border border-status-warning-border bg-status-warning-bg text-status-warning',
        invalid: 'border border-status-error-border bg-status-error-bg text-status-error',
        destructive: 'border border-status-error-border bg-status-error-bg text-status-error',
        role_admin:
          'border border-[var(--color-role-admin-border)] bg-[var(--color-role-admin-bg)] text-[var(--color-role-admin-fg)]',
        role_owner:
          'border border-[var(--color-role-owner-border)] bg-[var(--color-role-owner-bg)] text-[var(--color-role-owner-fg)]',
        role_member:
          'border border-[var(--color-role-member-border)] bg-[var(--color-role-member-bg)] text-[var(--color-role-member-fg)]',
      },
      size: {
        default: 'h-[22px] py-0 px-2.5 text-[12px] leading-none',
        sm: 'h-[18px] py-0 px-1.5 text-[10px] leading-none',
        lg: 'h-7 py-0 px-3 text-[12px] leading-none',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, size, ...props }: BadgeProps): ReactElement {
  return <span className={cn(badgeVariants({ variant, size }), className)} {...props} />;
}

const tagVariants = cva(
  'box-border inline-flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-[6px] font-medium leading-none align-middle transition-colors tabular-nums [&_svg]:block [&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'border border-subtle bg-surface-subtle text-fg-secondary',
        outline: 'border border-border bg-surface text-fg-secondary',
        accent: 'border border-brand-border-tint bg-brand-wash font-semibold text-brand',
        mono: 'border border-subtle bg-surface-subtle font-mono text-fg-secondary',
      },
      size: {
        default: 'h-[22px] py-0 px-2 text-[12px] leading-none',
        sm: 'h-[20px] py-0 px-1.5 text-[11px] leading-none',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface TagProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof tagVariants> {}

export function Tag({ className, variant, size, ...props }: TagProps): ReactElement {
  return <span className={cn(tagVariants({ variant, size }), className)} {...props} />;
}

export function Input({
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement>): ReactElement {
  return (
    <input
      className={cn(
        'box-border flex h-[36px] min-h-[36px] max-h-[36px] w-full min-w-0 rounded-control border border-border bg-surface-subtle px-3 py-0 text-[13px] leading-none text-fg transition-colors placeholder:text-fg-muted hover:border-border-medium focus:bg-surface focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-wash disabled:cursor-not-allowed disabled:opacity-50 tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

export function Spinner({
  size = 22,
  className,
}: {
  readonly size?: number;
  readonly className?: string;
}): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={cn('animate-spin text-brand', className)}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.22" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function LinearSpinner({ className }: { readonly className?: string }): ReactElement {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'relative h-[3px] w-full overflow-hidden rounded-t-card bg-brand-wash',
        className,
      )}
    >
      <div className="mp-linear-bar h-full w-1/2 rounded-pill bg-brand" />
    </div>
  );
}

export function PageLoader({
  label,
  ...props
}: HTMLAttributes<HTMLDivElement> & { readonly label: string }): ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[260px] w-full flex-col items-center justify-center gap-3 rounded-card border border-border bg-surface p-8 text-center shadow-card-base"
      {...props}
    >
      <Spinner size={28} />
      <span className="text-[13px] font-medium text-fg-secondary">{label}</span>
    </div>
  );
}

export interface ErrorBannerProps extends HTMLAttributes<HTMLDivElement> {
  readonly title?: string;
  readonly message: string;
  readonly nextStep?: string;
  readonly onRetry?: () => void;
  readonly retryLabel?: string;
}

export function ErrorBanner({
  title = 'Something went wrong',
  message,
  nextStep,
  onRetry,
  retryLabel = 'Try again',
  className,
  ...props
}: ErrorBannerProps): ReactElement {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 rounded-card border border-status-error-border bg-status-error-bg px-4 py-3 text-[13px] text-status-error',
        className,
      )}
      {...props}
    >
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="font-semibold">{title}</p>
        <p className="leading-relaxed">{message}</p>
        {nextStep !== undefined && <p className="text-[12px] opacity-90">{nextStep}</p>}
      </div>
      {onRetry !== undefined && (
        <Button type="button" size="sm" variant="secondary" onClick={onRetry} className="shrink-0">
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
  readonly icon?: ReactNode;
  readonly title: string;
  readonly description: string;
  readonly actionLabel?: string;
  readonly onAction?: () => void;
}

export function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  className,
  ...props
}: EmptyStateProps): ReactElement {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-card border border-dashed border-border bg-surface-subtle px-6 py-10 text-center',
        className,
      )}
      {...props}
    >
      {icon !== undefined && (
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full border border-subtle bg-surface text-fg-secondary">
          {icon}
        </div>
      )}
      <h3 className="text-[15px] font-semibold text-fg">{title}</h3>
      <p className="mt-1 max-w-md text-[13px] leading-relaxed text-fg-secondary">{description}</p>
      {actionLabel !== undefined && onAction !== undefined && (
        <Button type="button" variant="primary" size="sm" onClick={onAction} className="mt-4">
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

export interface SegmentedControlItem<T extends string | number> {
  readonly value: T;
  readonly label: ReactNode;
  readonly testId?: string;
}

export interface SegmentedControlProps<T extends string | number> extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  readonly value: T;
  readonly onChange: (value: T) => void;
  readonly items: readonly SegmentedControlItem<T>[];
  readonly ariaLabel: string;
  readonly role?: 'tablist' | 'group';
}

export function SegmentedControl<T extends string | number>({
  value,
  onChange,
  items,
  ariaLabel,
  role = 'group',
  className,
  ...props
}: SegmentedControlProps<T>): ReactElement {
  const isTabList = role === 'tablist';
  return (
    <div
      role={role}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex flex-wrap items-center gap-1 rounded-control border border-border bg-surface-subtle p-1',
        className,
      )}
      {...props}
    >
      {items.map((item) => {
        const selected = value === item.value;
        return (
          <Button
            key={String(item.value)}
            type="button"
            role={isTabList ? 'tab' : undefined}
            size="sm"
            variant={selected ? 'secondary' : 'ghost'}
            aria-selected={isTabList ? selected : undefined}
            aria-pressed={!isTabList ? selected : undefined}
            data-testid={item.testId}
            onClick={() => onChange(item.value)}
          >
            {item.label}
          </Button>
        );
      })}
    </div>
  );
}
